import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast
} from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { PlanFeatureAlert } from "../components/billing/PlanFeatureAlert.js";
import { PageHeader } from "../components/PageHeader.js";
import { FormField } from "../components/FormField.js";
import { apiFetch } from "../lib/api.js";
import { envLabel } from "../lib/requestPresentation.js";
import { showApiErrorToast } from "../lib/showApiErrorToast.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceBilling } from "../hooks/useWorkspaceBilling.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type ApproverGroupRow = {
  id: string;
  name: string;
  environment: string;
  members: Array<{ user_id: string }>;
};

type WorkspaceMemberRow = {
  user_id: string;
  email: string | null;
  name: string | null;
};

const memberLabel = (member: WorkspaceMemberRow): string =>
  member.name ?? member.email ?? member.user_id;

export const ApproverGroupsPage = () => {
  const { workspaceId, isLoading: widLoading } = useWorkspaceId();
  const { role } = useCurrentRole();
  const [env] = usePersistedAdminEnvironment();
  const [name, setName] = useState("");
  const [memberGroupId, setMemberGroupId] = useState<string | null>(null);
  const [memberUserId, setMemberUserId] = useState("");
  const queryClient = useQueryClient();
  const canManage = roleAtLeast(role, "admin");

  const billingQ = useWorkspaceBilling(workspaceId);
  const approvalGroupsEnabled = billingQ.features.approvalGroups;

  const q = useQuery({
    queryKey: ["approver-groups", workspaceId, env],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/approver-groups?environment=${env}`
      );
      if (!res.ok) {
        throw new Error(`groups_${String(res.status)}`);
      }
      return (await res.json()) as { groups: ApproverGroupRow[] };
    }
  });

  const membersQ = useQuery({
    queryKey: ["members", workspaceId],
    enabled: workspaceId !== null && canManage,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/members`);
      if (!res.ok) {
        throw new Error(`members_${String(res.status)}`);
      }
      return (await res.json()) as { members: WorkspaceMemberRow[] };
    }
  });

  const groups = q.data?.groups ?? [];
  const memberById = useMemo(() => {
    const map = new Map<string, WorkspaceMemberRow>();
    for (const m of membersQ.data?.members ?? []) {
      map.set(m.user_id, m);
    }
    return map;
  }, [membersQ.data?.members]);

  const activeGroup = memberGroupId !== null ? groups.find((g) => g.id === memberGroupId) : null;

  const availableMembersForActiveGroup = useMemo(() => {
    if (activeGroup === null) {
      return [];
    }
    const inGroup = new Set(activeGroup.members.map((m) => m.user_id));
    return (membersQ.data?.members ?? []).filter((m) => !inGroup.has(m.user_id));
  }, [activeGroup, membersQ.data?.members]);

  const createGroup = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/approver-groups`, {
        method: "POST",
        body: JSON.stringify({ environment: env, name: name.trim() })
      });
      if (!res.ok) {
        await showApiErrorToast(res);
        throw new Error(`create_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["approver-groups", workspaceId, env] });
      toast.show({ title: "Created", message: "Approver group created.", color: "green" });
    },
    onError: () => {
      /* toast shown from mutationFn */
    }
  });

  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/approver-groups/${groupId}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        await showApiErrorToast(res);
        throw new Error(`delete_${String(res.status)}`);
      }
    },
    onSuccess: (_data, groupId) => {
      if (memberGroupId === groupId) {
        setMemberGroupId(null);
        setMemberUserId("");
      }
      void queryClient.invalidateQueries({ queryKey: ["approver-groups", workspaceId, env] });
      toast.show({ title: "Removed", message: "Group deleted.", color: "green" });
    },
    onError: () => {
      /* toast shown from mutationFn */
    }
  });

  const addMember = useMutation({
    mutationFn: async (input: { groupId: string; userId: string }) => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/approver-groups/${input.groupId}/members`,
        {
          method: "POST",
          body: JSON.stringify({ user_id: input.userId })
        }
      );
      if (!res.ok) {
        await showApiErrorToast(res);
        throw new Error(`member_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      setMemberUserId("");
      setMemberGroupId(null);
      void queryClient.invalidateQueries({ queryKey: ["approver-groups", workspaceId, env] });
      toast.show({
        title: "Member added",
        message: "User can approve when this group is targeted.",
        color: "green"
      });
    },
    onError: () => {
      /* toast shown from mutationFn */
    }
  });

  const removeMember = useMutation({
    mutationFn: async (input: { groupId: string; userId: string }) => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/approver-groups/${input.groupId}/members/${input.userId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        await showApiErrorToast(res);
        throw new Error(`remove_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["approver-groups", workspaceId, env] });
      toast.show({ title: "Removed", message: "Member removed from group.", color: "green" });
    },
    onError: () => {
      /* toast shown from mutationFn */
    }
  });

  if (widLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (q.isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "45%" }} />
        <Skeleton style={{ height: 200, width: "100%" }} />
      </div>
    );
  }
  if (q.isError) {
    return <p className="text-sm text-destructive">Failed to load approver groups.</p>;
  }

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Approver groups"
        description="Named sets of approvers used in policy rules. Groups are scoped per environment—switch Sandbox or Production in the header."
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings/members">Workspace members</Link>
          </Button>
        }
      />

      {canManage ? (
        <PlanFeatureAlert
          feature="feature_approval_groups"
          enabled={approvalGroupsEnabled}
          billingLoaded={billingQ.isSuccess}
        />
      ) : null}

      {!canManage ? (
        <Alert>
          <AlertTitle>View only</AlertTitle>
          <AlertDescription>
            Your role is <strong>{role ?? "unknown"}</strong>. Only workspace admins and owners can
            create groups or change membership.
          </AlertDescription>
        </Alert>
      ) : null}

      {env === "production" ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTitle>Production environment</AlertTitle>
          <AlertDescription>
            Groups in production apply to live approval policies. Changes are recorded in the
            product audit log.
          </AlertDescription>
        </Alert>
      ) : null}

      {canManage ? (
        <section className="admin-panel" aria-labelledby="approver-groups-create-heading">
          <div className="admin-panel-header">
            <h2 id="approver-groups-create-heading" className="admin-panel-title">
              Create group
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
              Add a group in {envLabel(env)}, then assign members. Reference the group from policy
              approval rules.
            </p>
            <div className="admin-form-field-row">
              <FormField width="full" className="admin-form-field-wide">
                <Label htmlFor="group-name">Group name</Label>
                <Input
                  id="group-name"
                  placeholder="e.g. Finance approvers"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                />
              </FormField>
              <Button
                disabled={
                  !approvalGroupsEnabled || name.trim().length === 0 || createGroup.isPending
                }
                onClick={() => createGroup.mutate()}
                style={{ alignSelf: "flex-end" }}
              >
                {createGroup.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
                Create
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="admin-panel" aria-labelledby="approver-groups-list-heading">
        <div className="admin-panel-header">
          <h2 id="approver-groups-list-heading" className="admin-panel-title">
            {envLabel(env)} groups ({groups.length})
          </h2>
        </div>
        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Members</TableHead>
                {canManage ? <TableHead className="w-[200px]" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 3 : 2}>
                    <div className="admin-empty-state">
                      <p className="admin-empty-state-text">
                        No approver groups in {envLabel(env)} yet
                      </p>
                      {canManage ? (
                        <p className="admin-form-field-hint">Create one above to get started.</p>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell>
                      <span className="text-sm font-medium">{g.name}</span>
                    </TableCell>
                    <TableCell>
                      {g.members.length === 0 ? (
                        <span className="text-sm text-muted-foreground">No members</span>
                      ) : (
                        <ul className="admin-approver-member-list">
                          {g.members.map((m) => {
                            const row = memberById.get(m.user_id);
                            const label = row !== undefined ? memberLabel(row) : m.user_id;
                            return (
                              <li key={m.user_id} className="admin-approver-member-chip">
                                <span
                                  className="admin-approver-member-chip-label"
                                  title={m.user_id}
                                >
                                  {label}
                                </span>
                                {canManage ? (
                                  <button
                                    type="button"
                                    className="admin-approver-member-chip-remove"
                                    aria-label={`Remove ${label}`}
                                    disabled={removeMember.isPending}
                                    onClick={() => {
                                      removeMember.mutate({ groupId: g.id, userId: m.user_id });
                                    }}
                                  >
                                    ×
                                  </button>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </TableCell>
                    {canManage ? (
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={!approvalGroupsEnabled}
                            onClick={() => {
                              setMemberGroupId(g.id);
                              setMemberUserId("");
                            }}
                          >
                            Add member
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={!approvalGroupsEnabled || deleteGroup.isPending}
                            onClick={() => {
                              if (
                                globalThis.confirm(
                                  `Delete approver group “${g.name}”? Policies referencing it may fail until updated.`
                                )
                              ) {
                                deleteGroup.mutate(g.id);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {memberGroupId !== null && canManage && activeGroup !== null ? (
        <section className="admin-panel" aria-labelledby="approver-groups-add-member-heading">
          <div className="admin-panel-header">
            <h2 id="approver-groups-add-member-heading" className="admin-panel-title">
              Add member · {activeGroup.name}
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
              Choose a workspace member to add to this group. They can approve when a policy rule
              targets <strong>{activeGroup.name}</strong>.
            </p>
            {membersQ.isLoading ? (
              <Spinner />
            ) : availableMembersForActiveGroup.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Every workspace member is already in this group, or no members are available.{" "}
                <Link to="/settings/members" className="text-primary underline">
                  Invite members
                </Link>
                .
              </p>
            ) : (
              <div className="admin-form-field-row">
                <FormField width="full" className="admin-form-field-wide">
                  <Label>Member</Label>
                  <Select
                    value={memberUserId.length > 0 ? memberUserId : undefined}
                    onValueChange={setMemberUserId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a member" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMembersForActiveGroup.map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>
                          {memberLabel(m)}
                          {m.email !== null && m.name !== null ? ` (${m.email})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <div style={{ display: "flex", gap: "0.5rem", alignSelf: "flex-end" }}>
                  <Button
                    disabled={
                      !approvalGroupsEnabled || memberUserId.length === 0 || addMember.isPending
                    }
                    onClick={() => {
                      addMember.mutate({ groupId: memberGroupId, userId: memberUserId });
                    }}
                  >
                    {addMember.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
                    Add
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMemberGroupId(null);
                      setMemberUserId("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      ) : null}

      <section className="admin-panel" aria-labelledby="approver-groups-help-heading">
        <div className="admin-panel-header">
          <h2 id="approver-groups-help-heading" className="admin-panel-title">
            Using groups in policies
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <p className="admin-settings-row-desc" style={{ margin: 0 }}>
            In a conditional policy, set a rule&apos;s approval target to an approver group. Only
            members of that group (with the approver role or higher) can satisfy the approval step
            in {envLabel(env)}.
          </p>
        </div>
      </section>
    </div>
  );
};
